import moment from 'moment';
import i18next from 'i18next';

import {
  Account,
  EventDetails,
  PollingTicketDetails,
  Ticket,
  TicketArray,
} from 'types';
import { ImmutableQuery, MiscUtils } from 'services';
import {
  TICKETLIST_TICKETDETAILS_COMMON_PROPERTIES,
  CLOUD_ONLY_COMMON_PROPERTIES,
  TICKETLIST_MODIFIED_PROPERTY,
} from 'appConstants';
import { SortByValue } from 'components/shared';

const TicketUtils = {
  constructTextQuery: (text: string, hasPreviousId: boolean) => {
    const isTicketId = !!text && TicketUtils.isTicketId(text);

    /*
     * Clear all other filters when searching for a ticket id  only if the
     * previous search was not a ticket id
     */

    if (isTicketId && !hasPreviousId) {
      return {
        text,
        accountId: null,
        classification: null,
        favorite: null,
        modifiedEnd: null,
        modifiedStart: null,
        severity: null,
        status: null,
      };
    }

    return { text: text || null };
  },

  isTicketStatusClosed: (ticket: Ticket) =>
    ticket.status === i18next.t('common:closed'),

  isFavorite: (ticket: Ticket, overrides: {}) =>
    ticket.ticketId in overrides ? overrides[ticket.ticketId] : ticket.favorite,

  /** Test if string is a ticket id, allow spaces, but disallow any other characters at the end of the string */
  isTicketId: (value: string = '') => /\d{6}-\w{3}-\d{7}$/.test(value.trim()),

  /** Extract an array of resource Ids from the ticket's resource URIs array */
  getResourceIds: (resources: string[]): string[] =>
    resources.map((uri: string) => uri.split('/').pop() || ''),

  /** Return tickets array with mapped account data */
  mapAccounts: (tickets: Ticket[] = [], accounts: Account[] = []) =>
    tickets.map((ticket: Ticket) => ({
      ...ticket,
      account:
        accounts.find((item) => ticket.accountId === item.id) ||
        ({} as Account),
      resourceUris: ticket.resourceUris || [],
    })),

  searchHash: (sortBy: SortByValue) => `${sortBy.name}`.toLowerCase(),

  /** Handle query string pre-tickets request */
  preRequest: (query: ImmutableQuery) => {
    const end = query.get('modifiedEnd');
    const start = query.get('modifiedStart');

    let newQuery = query.clone();

    /** convert dates to ISO  */
    if (end) {
      const modifiedEnd = moment(MiscUtils.shortDateToIso(end))
        .endOf('day')
        .toISOString();
      newQuery = newQuery.set({ modifiedEnd });
    }

    if (start) {
      newQuery = newQuery.set({
        modifiedStart: MiscUtils.shortDateToIso(start),
      });
    }

    return newQuery;
  },

  /** Return boolean indicating if user has admin permission on a ticket */
  isAdmin: ({ permission }: Ticket) => permission === 'admin',

  /**
   * Checks for difference in data between Ticket selected in the list and it's details
   * If there is deference an object with a flag and a array of properties those are different
   */
  findTicketListAndDetailsDifference: (
    selectedTicket: Ticket,
    ticketList: TicketArray,
    isDedicated: boolean,
  ) => {
    const selectedTicketFromList =
      ticketList.find(
        (ticket: Ticket) => ticket.ticketId === selectedTicket.ticketId,
      ) || {};
    /*
     * Initially setting only the properties common
     * to cloud and dedicated ticket
     */

    let commonProperties = [...TICKETLIST_TICKETDETAILS_COMMON_PROPERTIES];
    let isDifferent = false;
    /*
     * If the account is of cloud type
     * then add in cloud only properties
     * to the commonProperties
     */
    if (!isDedicated) {
      commonProperties = [...commonProperties, ...CLOUD_ONLY_COMMON_PROPERTIES];
    }

    /*
     * Checking if data is different for
     * ticket in the list and ticket details
     */
    isDifferent = commonProperties.some((prop: string) => {
      return selectedTicketFromList[prop] !== selectedTicket[prop];
    });

    /*
     * Checking if the modified date of
     * ticket in the list and selected ticket.
     * We only want to update ticket list date
     * when it is less than that of ticket details.
     */
    if (
      moment(selectedTicketFromList[TICKETLIST_MODIFIED_PROPERTY]).isBefore(
        moment(selectedTicket[TICKETLIST_MODIFIED_PROPERTY]),
      )
    ) {
      commonProperties.push(TICKETLIST_MODIFIED_PROPERTY);
      isDifferent = true;
    }

    /*
     * If there is a difference in data
     * return a true flag and common properties
     */
    if (isDifferent) {
      return {
        isDifferent,
        commonProperties,
      };
    } else {
      return {
        // Else only return a false flag
        isDifferent,
      };
    }
  },

  /**
   * Return an object with the key from propertiesArray
   * and values from the ticket object
   */
  populateChangedTicketList: (propertiesArray: String[], ticket: Ticket) => {
    let changedPropertiesObject = {};
    propertiesArray.forEach((prop: string) => {
      changedPropertiesObject[prop] = ticket[prop];
    });

    return changedPropertiesObject;
  },

  /**
   * Clears input value based on previous and present value.
   */
  clearSelected: (prevValue: boolean, value: boolean) => !prevValue && value,

  /**
   * Generates or updates list of ticket ids, that will be
   * saved in localstorage for polling.
   */
  setPollingTicketList: (ticketId: string, eventId: string = '') => {
    const timeStamp = new Date(Date.now());
    const accountId = window.PORTAL_DATA.domainId;
    const ticketList: PollingTicketDetails[] = TicketUtils.getLocalStoragePollingDetails(
      accountId,
    );

    const eventDetail: EventDetails = {
      eventId,
      timeStamp,
    };
    const pollingTicketDetails: PollingTicketDetails = {
      ticketId: ticketId,
      eventDetails: eventId !== '' ? [eventDetail] : [],
    };
    if (ticketList.length) {
      const ticketIndex = ticketList.findIndex(
        (item: PollingTicketDetails) => item.ticketId === ticketId,
      );
      if (ticketIndex >= 0) {
        if (eventId !== '') {
          ticketList[ticketIndex].eventDetails.push(eventDetail);
        }
      } else {
        ticketList.push(pollingTicketDetails);
      }
    } else {
      ticketList.push(pollingTicketDetails);
    }
    TicketUtils.setLocalStoragePollingDetails(accountId, ticketList);
  },

  removeAccountFromPollingList: () => {
    const accountId = window.PORTAL_DATA.domainId;
    const pollingList = JSON.parse(
      localStorage.getItem('rsTicketPoll') || '{}',
    );
    if (
      Object.keys(pollingList).length &&
      pollingList.hasOwnProperty(accountId)
    ) {
      delete pollingList[accountId];
    }
    localStorage.setItem('rsTicketPoll', JSON.stringify(pollingList));
  },

  /**
   * Removes provided ticketId ticket from the
   * polling ticket list saved in the localstorage
   */
  removeTicketFromPollingList: (ticketId: string) => {
    const accountId = window.PORTAL_DATA.domainId;
    const ticketList: PollingTicketDetails[] = TicketUtils.getLocalStoragePollingDetails(
      accountId,
    );
    if (ticketList.length) {
      const ticketIndex = ticketList.findIndex(
        (item: PollingTicketDetails) => item.ticketId === ticketId,
      );
      if (ticketIndex >= 0) {
        ticketList.splice(ticketIndex, 1);
      }
      TicketUtils.setLocalStoragePollingDetails(accountId, ticketList);
    }
  },

  /**
   * Get polling details from localStorage
   */
  getLocalStoragePollingDetails: (
    accountId: string,
  ): PollingTicketDetails[] => {
    const pollingList = JSON.parse(
      localStorage.getItem('rsTicketPoll') || '{}',
    );
    return pollingList[accountId] || [];
  },

  /**
   * Set polling details to localStorage
   */
  setLocalStoragePollingDetails: (
    accountId: string,
    pollingDetails: PollingTicketDetails[],
  ) => {
    const pollingList = JSON.parse(
      localStorage.getItem('rsTicketPoll') || '{}',
    );
    pollingList[accountId] = [...pollingDetails];
    localStorage.setItem('rsTicketPoll', JSON.stringify(pollingList));
  },

  /*
   * Add Time Stamp when comment is updated,
   * add Time Stamp for Recipient change also
   */
  getTimeStampForTicketUpdate: (date: Date) => {
    const inputDate = moment(date);
    let commentDateAndTime = '';
    const today = moment();
    const yesterday = moment(today).subtract(1, 'day');
    if (inputDate.isSame(today, 'date')) {
      // today
      commentDateAndTime = `today at ${inputDate.format('LT')}`;
    } else if (inputDate.isSame(yesterday, 'date')) {
      // yesterday
      commentDateAndTime = `yesterday at ${inputDate.format('LT')}`;
    } else {
      commentDateAndTime = `${inputDate.format(
        'dddd, MMMM Do',
      )} ${inputDate.format('LT')}`;
    }
    return commentDateAndTime;
  },
};

export { TicketUtils };
